// Canned phrase-starter library for free-form narrative fields.
// Each entry is a STARTING POINT the RN edits — never a full sentence that would be
// submitted verbatim as PHI. Keys match NarrativeField fieldKey props.
//
// Rule-of-thumb: 3-6 bullets per field, phrased so the RN finishes them with
// concrete observations. All bullets are generic / context-free to avoid any
// risk of "default" PHI leaking into a submission.

export const NARRATIVE_SNIPPETS = {
  // Step 5 — Medical History & Meds
  "medical_history.chief_complaint": [
    "Member requires personal care assistance due to ",
    "Primary limitation affecting ADLs is ",
    "Functional decline over the past ___ months has resulted in ",
    "Member and caregiver identify ___ as the most pressing concern.",
  ],
  "medical_history.past_history": [
    "Relevant conditions: ",
    "Stable chronic conditions include ",
    "Recent changes in health status: ",
    "Notable for ___ diagnosed in ___ .",
  ],
  "medical_history.surgical_history": [
    "Significant surgical history: ",
    "No surgical procedures in the past 5 years.",
    "Post-op considerations affecting ADLs: ",
  ],
  "medical_history.hospitalizations": [
    "No hospitalizations in the past 12 months.",
    "Admitted ___ for ___ ; discharged on ___ to ___ .",
    "ER visits in the past 12 months: ",
  ],
  "medical_history.pain": [
    "Reports ___ /10 pain in ___ , triggered by ___ , relieved by ___ .",
    "Denies pain at rest; pain ___ /10 with ___ .",
    "Uses ___ for pain management; effective.",
    "Pain impacts ADL performance by ",
  ],

  // Step 3 — Nursing
  "nursing.notes": [
    "RN will provide oversight via ___ visits per ___ weeks.",
    "PCW has been trained and demonstrated competency on ",
    "Delegation plan documented per DHS 107.112: ",
    "Nurse available to PCW by phone during service hours.",
  ],
  "nursing.part_iii.comments": [
    "Training plan for Part III acts: RN will train PCW on ___ with return demonstration on ___ .",
    "Evaluation cadence: RN reassesses PCW competency every ___ visits / ___ weeks.",
    "Safety plan: If ___ occurs, PCW will immediately ___ and notify RN.",
    "Member/family has consented to this delegation plan.",
  ],
  "seizures.interventions": [
    "During a seizure, PCW will: (1) ensure member is safe from injury, (2) time the event, (3) place in recovery position after, (4) notify RN/EMS per plan.",
    "Post-ictal care includes: ",
    "Seizure log maintained by PCW and reviewed at each RN visit.",
  ],
  "pcst_notes": [
    "Additional considerations not captured elsewhere: ",
    "Coordination with ___ is in place for ",
    "Member/family strengths supporting the plan: ",
  ],

  // Step 6 — Social & Environment
  "social.home_environment": [
    "Single-family home / apartment / assisted living — ___ level(s), ___ steps at entry.",
    "Safety features in place: ___ . Missing / recommended: ___ .",
    "Bathroom accessibility: ___ . Bedroom on same level as living area: ___ .",
    "Emergency egress route: ___ . Smoke/CO detectors present.",
  ],
  "social.engagement": [
    "Member participates in ___ weekly; declines ___ due to ___ .",
    "Family contact: ___ visits ___ per week.",
    "Risk of social isolation: ",
    "Religious / cultural activities important to member: ",
  ],
  "f11136_other_info": [
    "Weekly hours requested are supported by the ADL/MOT scoring above.",
    "Client's condition trajectory: ",
    "Any change in hours would result in ",
  ],

  // Step 7 — CMS-485
  "cms485.dme_supplies": [
    "Walker — daily use.",
    "Bath bench + grab bars — bathroom.",
    "Hospital bed with rails — bedroom.",
    "Wheelchair — transfers and community mobility.",
    "Continence supplies — per utilization.",
  ],
  "cms485.safety_measures": [
    "Fall precautions: ___ .",
    "Seizure precautions (if applicable): ___ .",
    "Infection control: standard precautions at every visit.",
    "Emergency contact and plan posted in the home.",
  ],
  "cms485.nutritional_req": [
    "Regular diet; no restrictions.",
    "Diabetic (carb-consistent) diet; blood glucose monitored ___ .",
    "Low-sodium diet; fluid restriction of ___ /day.",
    "Mechanical soft / pureed / thickened-liquids consistency.",
  ],
  "cms485.orders_treatments": [
    "Personal care worker services ___ hours/week as detailed on F-11136.",
    "RN supervisory visit every ___ days.",
    "Delegated nursing acts per F-11133 Parts II and III.",
    "Notify MD for ___ .",
  ],
  "cms485.goals_discharge": [
    "Maintain current functional baseline for the ___ -day cert period.",
    "Prevent skin breakdown and falls.",
    "Discharge criteria: transfer to higher level of care, independence in ADLs, or death. Re-evaluation at ___ .",
  ],

  // Step 7 — Clinical Summary
  "clinical_summary.rationale": [
    "___ hrs/week is the minimum required to meet ADL deficits at PCST Levels ___ and prevent functional decline.",
    "Without PC at this level, member is at risk of ",
    "Informal caregiver provides ___ hrs/week; requested hours supplement but do not duplicate.",
  ],
  "clinical_summary.risks_if_denied": [
    "Increased fall risk due to ",
    "Skin breakdown risk from inability to reposition independently.",
    "ER utilization and potential admission for ",
    "Caregiver burnout leading to placement in facility-level care.",
  ],
  "clinical_summary.reassessment_plan": [
    "RN reassessment every ___ days; sooner if condition changes.",
    "Triggers for update to plan of care: ___ .",
    "Discharge planning initiated if ___ .",
  ],

  // Fallback
  default: [
    "Begin your observation with a specific time frame or event.",
    "Describe the functional impact before the clinical cause.",
    "End with an action step (PCW intervention, RN escalation, re-evaluation date).",
  ],
};

export function snippetsFor(fieldKey) {
  return NARRATIVE_SNIPPETS[fieldKey] || NARRATIVE_SNIPPETS.default;
}
